/**
 * #2067 — the ONE shared Blob → browser-download helper. Consolidates the two
 * previously duplicated anchor-click implementations in the customer portal
 * (`customer-account.store.ts` exportData and `customer-dashboard.store.ts`
 * downloadLatestResult), which had drifted only in filename/MIME.
 *
 * Behavior (kept exactly as the originals):
 * - a hidden `<a download>` is appended, clicked, and cleaned up on a deferred
 *   `setTimeout(0)` — revoking the object URL synchronously cancels the
 *   download in most browsers, so cleanup must NOT be synchronous;
 * - if anything throws before the deferred cleanup is scheduled, the anchor is
 *   removed and the object URL revoked immediately so the Blob URL never
 *   leaks, then the error is rethrown for the caller's own error handling.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  try {
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    // Defer cleanup so the browser has time to start the download.
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
      URL.revokeObjectURL(url);
    }, 0);
  } catch (e) {
    if (link.parentNode) {
      document.body.removeChild(link);
    }
    URL.revokeObjectURL(url);
    throw e;
  }
}

/** Mock pair for {@link installBlobUrlTestShim} — pass jest.fn()s from specs. */
export interface BlobUrlShimI {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
}

/**
 * #2067 — shared jsdom test shim (hoisted from the three duplicated copies in
 * the customer dashboard-ui specs). jsdom does not implement
 * `URL.createObjectURL`/`URL.revokeObjectURL`; this defines them from the
 * caller-supplied mocks when absent. Framework-agnostic on purpose (takes the
 * mock functions as arguments instead of referencing the `jest` global) so it
 * can live in a non-spec file without dragging test types into lib builds.
 */
export function installBlobUrlTestShim(shim: BlobUrlShimI): void {
  if (!URL.createObjectURL) {
    Object.defineProperty(URL, 'createObjectURL', { value: shim.createObjectURL, writable: true });
  }
  if (!URL.revokeObjectURL) {
    Object.defineProperty(URL, 'revokeObjectURL', { value: shim.revokeObjectURL, writable: true });
  }
}
